import React, { useEffect } from 'react';
import { BrandLogo } from './BrandLogo';
import { SfiLogo } from './SfiLogo';
import { DemoBadge } from './DemoBadge';

interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * First-visit-only branded splash. Distinct from `AboutModal` (reference
 * content, panel-triggered, always available): this is a one-shot
 * orientation moment when the user first lands at `/`. Persisted in
 * localStorage under `sfi.welcomeSeen` so it doesn't re-pop on every
 * page load. The deeper "What is this?" reference content remains
 * reachable from the explorer left-panel link after dismissal.
 *
 * Force-show in demos / QA via the `?welcome=1` query param (handled by
 * the App-level mount logic).
 */
export const WelcomeModal: React.FC<WelcomeModalProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2050] overflow-y-auto bg-black/75 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to the SFI Supply Shed Resilience Dashboard"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center px-4 py-6">
        <div
          className="relative w-full max-w-xl bg-figma-panel rounded-figma-panel shadow-figma-card border border-figma-lime/30 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Brand chrome strip */}
          <div className="h-1.5 bg-gradient-to-r from-figma-lime via-figma-lime to-figma-mint" aria-hidden="true" />

          <button
            onClick={onClose}
            aria-label="Close welcome dialog"
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-figma-text hover:bg-figma-card transition-smooth z-10"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>

          <div className="p-7 md:p-9">
            {/* Co-brand lockup */}
            <div className="flex items-center gap-4">
              <BrandLogo size={48} />
              <span aria-hidden="true" className="h-10 w-px bg-figma-ink-mute/40" />
              <SfiLogo size={48} />
              <DemoBadge />
            </div>

            <div className="mt-7">
              <span className="text-[11px] font-roboto font-bold uppercase tracking-[0.18em] text-figma-ink-mute">
                CTrees &times; SFI &middot; Proof of Concept
              </span>
              <h2 className="mt-2 font-anton font-normal text-[34px] md:text-[40px] leading-[1.05] tracking-[0.005em] uppercase text-figma-text m-0">
                Forest data,
                <br />
                mill by mill
              </h2>
            </div>

            <p className="mt-5 font-roboto text-body text-figma-ink-soft leading-relaxed">
              A working demo from CTrees and the Sustainable Forestry Initiative. Explore biomass, species,
              ownership, fire risk, and per-tree attributes within drive-time <span className="text-figma-text font-semibold">supply
              sheds</span> &mdash; the area each mill sources its wood from &mdash; around 10 US mills in
              California, Oregon, Georgia, and Alabama.
            </p>

            <ul className="mt-5 space-y-2.5 font-roboto text-body text-figma-ink-soft">
              <li className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-figma-lime flex-shrink-0" />
                <span>
                  <span className="text-figma-text font-semibold">12 layers</span> of forest data per supply shed.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-figma-yellow flex-shrink-0" />
                <span>
                  <span className="text-figma-text font-semibold">3 guided scenarios</span> walking through carbon, resilience, and per-tree carbon accounting.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-figma-mint flex-shrink-0" />
                <span>
                  <span className="text-figma-text font-semibold">Per-tree resolution</span> in California's Sierra Nevada &mdash; every crown attributed.
                </span>
              </li>
            </ul>

            <p className="mt-6 text-xs font-roboto text-figma-ink-mute">
              Prepared for the SFI annual conference. Some layers carry a &ldquo;Pilot preview&rdquo; tag &mdash; data is real
              but limited in coverage.
            </p>

            <div className="mt-7 flex items-center justify-end gap-4">
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 px-5 h-11 rounded-figma-button bg-figma-mint text-figma-panel font-roboto font-bold text-sm uppercase tracking-wider hover:bg-figma-mint/90 transition-smooth"
              >
                <span>Explore</span>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
